import type { ColumnDef, OnChangeFn, PaginationState } from "@tanstack/react-table";
import { useState } from "react";

import { DataTable } from "@/components/admin/DataTable";
import { SearchInput } from "@/components/admin/SearchInput";
import { type BadgeTone, StatusBadge } from "@/components/admin/StatusBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCancelScheduled,
  useEmailLog,
  useEmailSchedule,
  useResendEmail,
  useSendNow,
  useSuppressions,
  useUnsuppressEmail,
} from "@/hooks/admin/emailSequence";
import type { EmailLogItem, ScheduledEmailItem, SuppressionItem } from "@/types";

const PAGE_SIZE = 25;

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const SCHEDULE_TONE: Record<string, BadgeTone> = {
  planned: "info",
  sent: "success",
  failed: "warning",
  cancelled: "neutral",
  skipped: "neutral",
  suppressed: "warning",
};

const LOG_TONE: Record<string, BadgeTone> = {
  sent: "info",
  delivered: "success",
  bounced: "warning",
  complained: "warning",
  delivery_delayed: "neutral",
  failed: "warning",
};

const SCHEDULE_STATUSES = ["planned", "sent", "failed", "skipped", "cancelled", "suppressed"];
const LOG_STATUSES = ["sent", "delivered", "bounced", "complained", "delivery_delayed", "failed"];

function errorMessage(err: unknown, fallback: string) {
  return err instanceof Error ? err.message : fallback;
}

/** Build the {pageIndex,pageSize} state + change handler for 1-based `page`. */
function usePager(page: number, setPage: (p: number) => void) {
  const pagination: PaginationState = { pageIndex: page - 1, pageSize: PAGE_SIZE };
  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPage(next.pageIndex + 1);
  };
  return { pagination, onPaginationChange };
}

function StatusFilter({
  value,
  statuses,
  onChange,
}: {
  value: string;
  statuses: string[];
  onChange: (v: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-44">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="ALL">All statuses</SelectItem>
        {statuses.map((s) => (
          <SelectItem key={s} value={s}>
            {s}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ScheduleTab() {
  // Default to `planned` so live emails aren't buried under old skipped rows.
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("planned");
  const [search, setSearch] = useState("");
  const query = useEmailSchedule({
    page,
    page_size: PAGE_SIZE,
    status: status === "ALL" ? undefined : status,
    q: search || undefined,
  });
  const sendNow = useSendNow();
  const cancel = useCancelScheduled();
  const data = query.data;
  const { pagination, onPaginationChange } = usePager(page, setPage);

  const columns: ColumnDef<ScheduledEmailItem>[] = [
    {
      accessorKey: "scheduled_for",
      header: "Scheduled",
      cell: ({ row }) => formatDate(row.original.scheduled_for),
    },
    { accessorKey: "template_id", header: "Template" },
    {
      accessorKey: "to_email",
      header: "Recipient",
      cell: ({ row }) => row.original.to_email ?? "—",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <StatusBadge tone={SCHEDULE_TONE[row.original.status] ?? "neutral"}>
          {row.original.status}
        </StatusBadge>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        row.original.status === "planned" ? (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => sendNow.mutate(row.original.id)}
              disabled={sendNow.isPending}
              className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
            >
              Send now
            </button>
            <button
              type="button"
              onClick={() => cancel.mutate(row.original.id)}
              disabled={cancel.isPending}
              className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <DataTable
      className="h-full"
      columns={columns}
      data={data?.items ?? []}
      isLoading={query.isPending}
      error={query.isError ? errorMessage(query.error, "Failed to load schedule") : null}
      onRetry={() => query.refetch()}
      emptyMessage="No scheduled emails match the current filters"
      getRowId={(r) => r.id}
      toolbar={
        <div className="flex items-center justify-between gap-3">
          <SearchInput
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search recipient or template…"
          />
          <StatusFilter
            value={status}
            statuses={SCHEDULE_STATUSES}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
          />
        </div>
      }
      manualPagination
      pageCount={data?.total_pages ?? 0}
      rowCount={data?.total ?? 0}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
    />
  );
}

export function LogTab() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("ALL");
  const [search, setSearch] = useState("");
  const query = useEmailLog({
    page,
    page_size: PAGE_SIZE,
    status: status === "ALL" ? undefined : status,
    q: search || undefined,
  });
  const resend = useResendEmail();
  const data = query.data;
  const { pagination, onPaginationChange } = usePager(page, setPage);

  const columns: ColumnDef<EmailLogItem>[] = [
    {
      accessorKey: "created_at",
      header: "Sent",
      cell: ({ row }) => formatDate(row.original.sent_at ?? row.original.created_at),
    },
    {
      accessorKey: "template_id",
      header: "Template",
      cell: ({ row }) => row.original.template_id ?? "—",
    },
    { accessorKey: "to_email", header: "Recipient" },
    {
      accessorKey: "status",
      header: "Delivery",
      cell: ({ row }) => (
        <StatusBadge tone={LOG_TONE[row.original.status] ?? "neutral"}>
          {row.original.status}
        </StatusBadge>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => resend.mutate(row.original.id)}
            disabled={resend.isPending}
            className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
          >
            Resend
          </button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      className="h-full"
      columns={columns}
      data={data?.items ?? []}
      isLoading={query.isPending}
      error={query.isError ? errorMessage(query.error, "Failed to load log") : null}
      onRetry={() => query.refetch()}
      emptyMessage="No emails sent yet"
      getRowId={(r) => r.id}
      toolbar={
        <div className="flex items-center justify-between gap-3">
          <SearchInput
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search recipient or subject…"
          />
          <StatusFilter
            value={status}
            statuses={LOG_STATUSES}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
          />
        </div>
      }
      manualPagination
      pageCount={data?.total_pages ?? 0}
      rowCount={data?.total ?? 0}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
    />
  );
}

export function SuppressionTab() {
  const query = useSuppressions();
  const unsuppress = useUnsuppressEmail();

  const columns: ColumnDef<SuppressionItem>[] = [
    { accessorKey: "email", header: "Address" },
    { accessorKey: "reason", header: "Reason", cell: ({ row }) => row.original.reason ?? "—" },
    {
      accessorKey: "created_at",
      header: "Since",
      cell: ({ row }) => formatDate(row.original.created_at),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => unsuppress.mutate(row.original.email)}
            disabled={unsuppress.isPending}
            className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
          >
            Unsuppress
          </button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      className="h-full"
      columns={columns}
      data={query.data?.items ?? []}
      isLoading={query.isPending}
      error={query.isError ? errorMessage(query.error, "Failed to load suppressions") : null}
      onRetry={() => query.refetch()}
      emptyMessage="No suppressed addresses"
      getRowId={(r) => r.id}
    />
  );
}
